import { useMutation } from '@apollo/client';
import {
    ModalContent,
    ModalTitle,
    PrimaryButton,
    ResponsiveSidebarDialog,
    SecondaryButton,
    StickyModalActions,
} from '@/styles/blocks';
import { css } from '@emotion/react';
import { captureException } from '@sentry/react';
import { Grid, IconButton, MenuItem, TextField, Select } from '@material-ui/core';
import { Body1, SectionHeader } from '../../blocks';
import { ACCEPT_CLAIM } from './graphql';
import React, { useState, useMemo, useEffect, useContext } from 'react';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import { currencyFormatter } from '../../formatters';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { colors } from '@/styles';
import { UserContext } from '@/components/App';
import { isFinite } from 'lodash';
import { OnwardToggle } from '@/components/ShipmentForm/blocks';

const AcceptClaimModal = ({ claim, open, onClose, onSave, callbacks }) => {
    const [adjustments, setAdjustments] = useState([]);
    const [reimbursements, setReimbursements] = useState([]);
    const [onward_responsible, setOnwardResponsible] = useState(false);
    const { user } = useContext(UserContext);

    useEffect(() => {
        setAdjustments([]);
        setReimbursements([]);
        setOnwardResponsible(false);
    }, [claim]);

    useEffect(() => {
        if (onward_responsible) {
            setReimbursements([]);
        }
    }, [onward_responsible]);

    const itemsTotal = useMemo(() => {
        return (claim?.items || []).reduce((acc, item) => {
            return acc + (item.amount || 0);
        }, 0);
    }, [claim]);

    const isValid = useMemo(() => {
        return (
            adjustments.every((adjustment) => isFinite(adjustment.amount)) &&
            reimbursements.every((reimbursement) => isFinite(reimbursement.amount))
        );
    }, [adjustments, reimbursements]);

    const [acceptClaim, { loading: saveInFlight }] = useMutation(ACCEPT_CLAIM, {
        onError: (error) => {
            console.error(error);
            captureException(error);
            callbacks.setNotification({
                severity: 'error',
                message: `Failed to update claim. ${error.message}`,
            });
        },
    });

    const onAccept = () => {
        const NOW = new Date();
        acceptClaim({
            variables: {
                claim_id: claim.claim_id,
                update: {
                    claim_status: 'ACCEPTED',
                    completed_by: user.user_id,
                    onward_responsible: onward_responsible,
                },
                adjustments: [
                    ...adjustments.map((adjustment, i) => ({
                        claim_id: claim.claim_id,
                        type: 'ADJUSTMENT',
                        amount: adjustment.amount,
                        reason: adjustment.reason,
                        invoice_timestamp: new Date(NOW.getTime() + i * 7 * 24 * 60 * 60 * 1000).toISOString(),
                    })),
                    ...reimbursements.map((reimbursement, i) => ({
                        claim_id: claim.claim_id,
                        type: 'REIMBURSEMENT',
                        amount: reimbursement.amount,
                        reason: reimbursement.reason,
                        invoice_timestamp: new Date(NOW.getTime() + i * 7 * 24 * 60 * 60 * 1000).toISOString(),
                    })),
                ],
            },
        }).then(() => {
            onSave();
        });
    };

    if (!claim) return null;

    return (
        <ResponsiveSidebarDialog open={open} onClose={onClose}>
            <ModalTitle title={'Accept Claim'} onClose={onClose} />
            <ModalContent
                css={css`
                    width: 600px;
                `}
            >
                <Grid container direction="column" css={css``}>
                    <Grid
                        container
                        css={css`
                            justify-content: space-between;
                        `}
                    >
                        <Grid item>
                            <Body1>Requested Amount for Item(s)</Body1>
                        </Grid>
                        <Grid item>
                            <Body1>{currencyFormatter(itemsTotal)}</Body1>
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        css={css`
                            justify-content: space-between;
                        `}
                    >
                        <Grid item>
                            <Body1>Requested Freight Amount</Body1>
                        </Grid>
                        <Grid item>
                            <Body1>{currencyFormatter(claim.freight_amount)}</Body1>
                        </Grid>
                    </Grid>
                    <hr />
                    <Grid
                        container
                        css={css`
                            justify-content: space-between;
                            margin-bottom: 1rem;
                        `}
                    >
                        <Grid item>
                            <Body1>Total</Body1>
                        </Grid>
                        <Grid item>
                            <Body1>{currencyFormatter(itemsTotal + claim.freight_amount)}</Body1>
                        </Grid>
                    </Grid>
                    {!onward_responsible && (
                        <>
                            <Grid
                                item
                                css={css`
                                    margin-top: 1rem;
                                    margin-bottom: 1rem;
                                `}
                            >
                                <TextField
                                    variant="outlined"
                                    select
                                    fullWidth
                                    label="Subtract from carrier's next or future payments?"
                                    value={adjustments.length > 1 ? 'Future' : adjustments.length === 1 ? 'Next' : ''}
                                    onChange={(e) => {
                                        if (e.target.value === 'Future') {
                                            setAdjustments([
                                                {
                                                    amount: (itemsTotal + claim.freight_amount) / 2,
                                                },
                                                {
                                                    amount: (itemsTotal + claim.freight_amount) / 2,
                                                },
                                            ]);
                                        } else {
                                            setAdjustments([
                                                {
                                                    amount: itemsTotal + claim.freight_amount,
                                                },
                                            ]);
                                        }
                                    }}
                                >
                                    <MenuItem value="Next">Next Payment</MenuItem>
                                    <MenuItem value="Future">Future Payment(s)</MenuItem>
                                </TextField>
                            </Grid>
                            {adjustments.length > 1 && (
                                <>
                                    <Grid
                                        item
                                        css={css`
                                            margin-bottom: 0.5rem;
                                        `}
                                    >
                                        <SectionHeader>Payments</SectionHeader>
                                    </Grid>
                                    {adjustments.map((adjustment, i) => (
                                        <Grid
                                            container
                                            key={i}
                                            css={css`
                                                align-items: center;
                                                margin-bottom: 1rem;
                                            `}
                                        >
                                            <Grid
                                                item
                                                css={css`
                                                    width: 2rem;
                                                    display: flex;
                                                    justify-content: center;
                                                `}
                                            >
                                                <Body1>{i + 1}</Body1>
                                            </Grid>
                                            <Grid
                                                item
                                                css={css`
                                                    width: 10rem;
                                                `}
                                            >
                                                <CurrencyTextField
                                                    variant="outlined"
                                                    fullWidth
                                                    currencySymbole="$"
                                                    outputFormat="number"
                                                    size="small"
                                                    value={adjustment.amount}
                                                    error={!isFinite(adjustment.amount)}
                                                    onChange={(e) => {
                                                        const newAdjustments = [...adjustments];
                                                        newAdjustments[i] = {
                                                            ...adjustment,
                                                            amount: parseFloat(e.target.value),
                                                        };
                                                        setAdjustments(newAdjustments);
                                                    }}
                                                />
                                            </Grid>
                                            {i === adjustments.length - 1 && i > 1 && (
                                                <Grid item>
                                                    <IconButton
                                                        onClick={() =>
                                                            setAdjustments(adjustments.slice(0, adjustments.length - 1))
                                                        }
                                                    >
                                                        <RemoveCircleIcon
                                                            css={css`
                                                                color: ${colors.reds.primary};
                                                            `}
                                                        />
                                                    </IconButton>
                                                </Grid>
                                            )}
                                        </Grid>
                                    ))}
                                    <Grid
                                        item
                                        css={css`
                                            margin-bottom: 1rem;
                                        `}
                                    >
                                        <PrimaryButton onClick={() => setAdjustments([...adjustments, { amount: 0 }])}>
                                            Add Payment
                                        </PrimaryButton>
                                    </Grid>
                                </>
                            )}
                            <Grid
                                item
                                css={css`
                                    margin-top: 1rem;
                                    margin-bottom: 1rem;
                                `}
                            >
                                <TextField
                                    variant="outlined"
                                    select
                                    fullWidth
                                    label="Reimburse shipper on next or future payments?"
                                    value={
                                        reimbursements.length > 1 ? 'Future' : reimbursements.length === 1 ? 'Next' : ''
                                    }
                                    onChange={(e) => {
                                        if (e.target.value === 'Future') {
                                            setReimbursements([
                                                {
                                                    amount: (itemsTotal + claim.freight_amount) / 2,
                                                },
                                                {
                                                    amount: (itemsTotal + claim.freight_amount) / 2,
                                                },
                                            ]);
                                        } else {
                                            setReimbursements([
                                                {
                                                    amount: itemsTotal + claim.freight_amount,
                                                },
                                            ]);
                                        }
                                    }}
                                >
                                    <MenuItem value="Next">Next Payment</MenuItem>
                                    <MenuItem value="Future">Future Payment(s)</MenuItem>
                                </TextField>
                            </Grid>
                            {reimbursements.length > 1 && (
                                <>
                                    <Grid
                                        item
                                        css={css`
                                            margin-bottom: 0.5rem;
                                        `}
                                    >
                                        <SectionHeader>Reimbursements</SectionHeader>
                                    </Grid>
                                    {reimbursements.map((reimbursement, i) => (
                                        <Grid
                                            container
                                            key={i}
                                            css={css`
                                                align-items: center;
                                                margin-bottom: 1rem;
                                            `}
                                        >
                                            <Grid
                                                item
                                                css={css`
                                                    width: 2rem;
                                                    display: flex;
                                                    justify-content: center;
                                                `}
                                            >
                                                <Body1>{i + 1}</Body1>
                                            </Grid>
                                            <Grid
                                                item
                                                css={css`
                                                    width: 10rem;
                                                `}
                                            >
                                                <CurrencyTextField
                                                    variant="outlined"
                                                    fullWidth
                                                    currencySymbole="$"
                                                    outputFormat="number"
                                                    size="small"
                                                    value={reimbursement.amount}
                                                    error={!isFinite(reimbursement.amount)}
                                                    onChange={(e) => {
                                                        const newReimbursements = [...reimbursements];
                                                        newReimbursements[i] = {
                                                            ...reimbursement,
                                                            amount: parseFloat(e.target.value),
                                                        };
                                                        setReimbursements(newReimbursements);
                                                    }}
                                                />
                                            </Grid>
                                            {i === reimbursements.length - 1 && i > 1 && (
                                                <Grid item>
                                                    <IconButton
                                                        onClick={() =>
                                                            setReimbursements(
                                                                reimbursements.slice(0, reimbursements.length - 1)
                                                            )
                                                        }
                                                    >
                                                        <RemoveCircleIcon
                                                            css={css`
                                                                color: ${colors.reds.primary};
                                                            `}
                                                        />
                                                    </IconButton>
                                                </Grid>
                                            )}
                                        </Grid>
                                    ))}
                                    <Grid item>
                                        <PrimaryButton
                                            onClick={() => setReimbursements([...reimbursements, { amount: 0 }])}
                                        >
                                            Add Reimbursement
                                        </PrimaryButton>
                                    </Grid>
                                </>
                            )}
                        </>
                    )}
                    <Grid
                        item
                        css={css`
                            margin-top: 1rem;
                            margin-bottom: 1rem;
                            display: flex;
                            align-items: center;
                            justify-content: flex-start;
                            flex-direction: row;
                            gap: 12px;
                        `}
                    >
                        <Body1>Onward Responsible?</Body1>
                        <OnwardToggle
                            value={onward_responsible}
                            onChange={(e) => setOnwardResponsible(e.target.checked)}
                        />
                    </Grid>
                </Grid>
            </ModalContent>
            <StickyModalActions>
                <Grid item>
                    <SecondaryButton disabled={saveInFlight} onClick={onClose}>
                        Cancel
                    </SecondaryButton>
                </Grid>
                <Grid item>
                    <PrimaryButton disabled={saveInFlight || !isValid} onClick={onAccept}>
                        Approve
                    </PrimaryButton>
                </Grid>
            </StickyModalActions>
        </ResponsiveSidebarDialog>
    );
};

export default AcceptClaimModal;
